export const theme = {
  colors: {
    // Brand System — burnt terracotta primary, zinc greys
    primary: "#BF4E30",       // burnt terracotta
    secondary: "#10B981",     // emerald — unchanged
    accent: "#D2684A",        // terracotta-mid
    background: "#f8fafc",    // slate-50
    surface: "#FFFFFF",
    navigationSurface: "#E8E3DB",
    navigationOutline: "#C9C1B7",
    navigationText: "#5D5A57",
    error: "#EF4444",
    reject: "#18181b",        // zinc-900
    text: "#18181b",          // zinc-900
    disabled: "#a1a1aa",      // zinc-400
    placeholder: "#a1a1aa",   // zinc-400
    backdrop: "rgba(0, 0, 0, 0.4)",
    notification: "#BF4E30",  // brand
    success: "#3B82F6",
    warning: "#F59E0B",
    info: "#3B82F6",
    infoContainer: "#EFF6FF",
    /** Vibrant local chip green (#4CAF50) — pre-06-2026 profile listing badges */
    localVibrant: "#4CAF50",
    /** Local listing card tint — matches AddServiceScreen localTileBackground */
    localContainer: "rgba(76, 175, 80, 0.12)",
    /** Deeper eucalyptus green — category/status chips and listing card backgrounds */
    eucalyptus: "#486B5A",
    eucalyptusContainer: "rgba(72, 107, 90, 0.12)",
    /** Featured chip — warm sandy brown, complements brand orange */
    featuredSandy: "#A07850",
    featuredContainer: "rgba(160, 120, 80, 0.12)",
    /** Events tier — violet, distinct from pro blue / local green / featured brown */
    eventViolet: "#8B5CF6",
    eventContainer: "rgba(139, 92, 246, 0.12)",
    // Brand scale — available to any screen
    brand:      "#BF4E30",
    brandDark:  "#A83E1B",
    brandMid:   "#D2684A",
    brandPale:  "#E8B9A8",
    brandMuted: "#FBE9E7",
    // Alias used throughout the app for tinted container backgrounds
    primaryContainer: "#FBE9E7",  // brand-muted — terracotta tint
    onPrimaryContainer: "#5D1300", // deep terracotta-brown — readable on primaryContainer
    // Zinc grey scale
    surfaceVariant: "#f4f4f5",    // zinc-100
    onSurfaceVariant: "#71717a",  // zinc-500
    outline: "#e4e4e7",           // zinc-200
    outlineVariant: "#f4f4f5",    // zinc-100
    inverseSurface: "#18181b",    // zinc-900
    inverseOnSurface: "#fafafa",  // zinc-50
    inversePrimary: "#D2684A",    // terracotta-mid
    shadow: "#000000",
    scrim: "#000000",
    surfaceTint: "#BF4E30",       // brand
    onPrimary: "#FFFFFF",
    onSecondary: "#FFFFFF",
    onSurface: "#18181b",         // zinc-900
    onSurfaceTint: "#FFFFFF",
  },
  // Numeric font size scale — use these for fontSize: values
  fontSizes: {
    xs:   11,  // timestamps, badges, legal
    sm:   12,  // captions, helper text
    md:   13,  // secondary body
    base: 14,  // default body
    lg:   15,  // comfortable body
    xl:   16,  // emphasized body / small labels
    xxl:  18,  // subheadings
    h3:   20,  // section headings
    h2:   24,  // card headings
    h1:   28,  // page headings
    display: 32, // hero/display
  },
  fonts: {
    regular: {
      fontFamily:
        '"Poppins", "Helvetica Neue", "Segoe UI", "Roboto", sans-serif',
      fontWeight: "400",
    },
    medium: {
      fontFamily:
        '"Poppins", "Helvetica Neue", "Segoe UI", "Roboto", sans-serif',
      fontWeight: "500",
    },
    light: {
      fontFamily:
        '"Poppins", "Helvetica Neue", "Segoe UI", "Roboto", sans-serif',
      fontWeight: "300",
    },
    thin: {
      fontFamily:
        '"Poppins", "Helvetica Neue", "Segoe UI", "Roboto", sans-serif',
      fontWeight: "200",
    },
    labelLarge: {
      fontFamily:
        '"Poppins", "Helvetica Neue", "Segoe UI", "Roboto", sans-serif',
      fontWeight: "700",
      fontSize: 18,
      lineHeight: 24,
    },
    labelMedium: {
      fontFamily:
        '"Poppins", "Helvetica Neue", "Segoe UI", "Roboto", sans-serif',
      fontWeight: "700",
      fontSize: 16,
      lineHeight: 20,
    },
    labelSmall: {
      fontFamily:
        '"Poppins", "Helvetica Neue", "Segoe UI", "Roboto", sans-serif',
      fontWeight: "700",
      fontSize: 14,
      lineHeight: 18,
    },
    bodyLarge: {
      fontFamily:
        '"Poppins", "Helvetica Neue", "Segoe UI", "Roboto", sans-serif',
      fontWeight: "400",
      fontSize: 20,
      lineHeight: 28,
    },
    bodyMedium: {
      fontFamily:
        '"Poppins", "Helvetica Neue", "Segoe UI", "Roboto", sans-serif',
      fontWeight: "400",
      fontSize: 18,
      lineHeight: 24,
    },
    bodySmall: {
      fontFamily:
        '"Poppins", "Helvetica Neue", "Segoe UI", "Roboto", sans-serif',
      fontWeight: "400",
      fontSize: 16,
      lineHeight: 20,
    },
    titleLarge: {
      fontFamily:
        '"Poppins", "Helvetica Neue", "Segoe UI", "Roboto", sans-serif',
      fontWeight: "700",
      fontSize: 26,
      lineHeight: 32,
    },
    titleMedium: {
      fontFamily:
        '"Poppins", "Helvetica Neue", "Segoe UI", "Roboto", sans-serif',
      fontWeight: "700",
      fontSize: 20,
      lineHeight: 28,
    },
    titleSmall: {
      fontFamily:
        '"Poppins", "Helvetica Neue", "Segoe UI", "Roboto", sans-serif',
      fontWeight: "400",
      fontSize: 18,
      lineHeight: 24,
    },
    headlineLarge: {
      fontFamily:
        '"Poppins", "Helvetica Neue", "Segoe UI", "Roboto", sans-serif',
      fontWeight: "700",
      fontSize: 36,
      lineHeight: 44,
    },
    headlineMedium: {
      fontFamily:
        '"Poppins", "Helvetica Neue", "Segoe UI", "Roboto", sans-serif',
      fontWeight: "700",
      fontSize: 32,
      lineHeight: 40,
    },
    headlineSmall: {
      fontFamily:
        '"Poppins", "Helvetica Neue", "Segoe UI", "Roboto", sans-serif',
      fontWeight: "700",
      fontSize: 28,
      lineHeight: 36,
    },
  },
  sizes: {
    listingBadgeHeight: 30,
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
    xxxl: 64,
    // In-between values used throughout the app
    xs2: 12,   // between xs(4) and md(16)
    xs3: 20,   // between md(16) and lg(24)
  },
  borderRadius: {
    none: 0,
    sm: 6,
    card: 8,   // most common card/chip radius
    input: 10, // form inputs
    md: 12,
    lg: 16,
    xl: 20,
    pill: 28,  // FABs and pill buttons
    xxl: 24,
    full: 9999,
  },
  shadows: {
    // Modern 2025 shadow system
    none: {
      shadowColor: "transparent",
      shadowOffset: { width: 0, height: 0 },
      shadowOpacity: 0,
      shadowRadius: 0,
      elevation: 0,
    },
    small: {
      shadowColor: "#000000",
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.05,
      shadowRadius: 2,
      elevation: 1,
    },
    medium: {
      shadowColor: "#000000",
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.08,
      shadowRadius: 8,
      elevation: 3,
    },
    large: {
      shadowColor: "#000000",
      shadowOffset: { width: 0, height: 8 },
      shadowOpacity: 0.12,
      shadowRadius: 16,
      elevation: 6,
    },
    xlarge: {
      shadowColor: "#000000",
      shadowOffset: { width: 0, height: 16 },
      shadowOpacity: 0.15,
      shadowRadius: 24,
      elevation: 12,
    },
  },
  components: {
    card: {
      backgroundColor: "#FFFFFF",
      borderRadius: 16,
      padding: 20,
      marginVertical: 12,
      marginHorizontal: 16,
      borderWidth: 1,
      borderColor: "#F3F4F6",
    },
    button: {
      borderRadius: 12,
      paddingVertical: 14,
      paddingHorizontal: 28,
      minHeight: 48,
    },
    input: {
      backgroundColor: "#FFFFFF",
      borderRadius: 12,
      borderWidth: 1,
      borderColor: "#E5E7EB",
      paddingHorizontal: 16,
      paddingVertical: 12,
    },
    chip: {
      borderRadius: 20,
      paddingHorizontal: 16,
      paddingVertical: 8,
    },
    fab: {
      borderRadius: 28,
      elevation: 6,
    },
  },
  // Modern 2025 design tokens
  elevation: {
    level0: 0,
    level1: 1,
    level2: 3,
    level3: 6,
    level4: 8,
    level5: 12,
  },
  // Category colour palette — single source of truth for listing/event chips
  categoryColors: {
    workshop:      "#D97706", // amber-600
    sponsoredAd:   "#7C3AED", // violet-700
    fundraiser:    "#DB2777", // pink-600
    service:       "#0284C7", // sky-600
    trade:         "#059669", // emerald-600
    food:          "#65A30D", // lime-600
    event:         "#8B5CF6", // violet-500 — community events tier
    community:     "#2563EB", // blue-600
    job:           "#9333EA", // purple-600
    general:       "#71717a", // zinc-500
  },
  chipColors: {
    tier: {
      pro:       "#3B82F6",
      local:     "#4CAF50",
      sponsored: "#D97706",
      featured:  "#A07850",
      event:     "#8B5CF6",
    },
    exchange: {
      payment:   "#BF4E30",
      barter:    "#BF4E30",
      volunteer: "#BF4E30",
      gift:      "#71717a",
      both:      "#BF4E30",
    },
  },
  // Modern color variants
  colorVariants: {
    primary: {
      container: "#FBE9E7",   // brand-muted
      onContainer: "#5D1300", // deep terracotta-brown for contrast
    },
    secondary: {
      container: "#ECFDF5",
      onContainer: "#064E3B",
    },
    tertiary: {
      container: "#FEF3C7",
      onContainer: "#92400E",
    },
    error: {
      container: "#FEF2F2",
      onContainer: "#7F1D1D",
    },
  },
  // Accessibility enhancements
  accessibility: {
    focusRing: {
      borderWidth: 0,
      borderColor: "transparent",
      borderRadius: 0,
    },
    focusRingInset: {
      borderWidth: 0,
      borderColor: "transparent",
      borderRadius: 0,
    },
    minimumTouchTarget: 44,
    highContrast: {
      primary: "#000000",
      onPrimary: "#FFFFFF",
      surface: "#FFFFFF",
      onSurface: "#000000",
      secondary: "#000000",
      onSecondary: "#FFFFFF",
      background: "#FFFFFF",
      onBackground: "#000000",
      surfaceVariant: "#F0F0F0",
      onSurfaceVariant: "#000000",
      outline: "#000000",
      error: "#D32F2F",
      onError: "#FFFFFF",
    },
    // Color contrast verification (WCAG AA compliant)
    contrastRatios: {
      primaryOnSurface: 8.2, // Burnt terracotta (#BF4E30) on white
      textOnBackground: 12.6, // Gray 800 on Gray 100
      secondaryOnSurface: 4.8, // Emerald 500 on Gray 100
      errorOnSurface: 4.2, // Red 500 on Gray 100
    },
  },
};
